import React from 'react';

import { LOCALE } from '@strava/constants';
import { devLog } from '@strava/utils/development';

import {
  AnalyticsContextValue,
  AnalyticsProvider
} from './StravaAnalyticsContext.provider';
import {
  BranchContextValue,
  BranchProvider
} from './StravaBranchContext.provider';
import {
  ErrorLoggingContextValue,
  ErrorLoggingProvider
} from './StravaErrorLoggingContext.provider';
import {
  ExperimentContextValue,
  ExperimentProvider
} from './StravaExperimentContext.provider';
import {
  FeaturesContextValue,
  FeaturesProvider
} from './StravaFeaturesContext.provider';
import {
  TranslationContextValue,
  TranslationProvider
} from './StravaTranslationContext.provider';

/**
 * This is mocking strava-container context values that exist in active and nextJS apps.
 * These are used to provide default values for components mounted on unit tests and also
 * for bootstrapping microfrontends in development mode.
 *
 * Read: https://github.com/strava/web/blob/main/microfrontends/README.md#Mocked-Context
 */

interface MockedContextProviderProps {
  children: React.ReactNode;
  analyticsContextValue?: Partial<AnalyticsContextValue>;
  translationContextValue?: Partial<TranslationContextValue>;
  errorLoggingContextValue?: Partial<ErrorLoggingContextValue>;
  branchContextValue?: Partial<BranchContextValue>;
  experimentContextValue?: Partial<ExperimentContextValue>;
  featuresContextValue?: Partial<FeaturesContextValue>;
}

export const MockedContextProvider = ({
  children,
  analyticsContextValue = {},
  translationContextValue = {},
  errorLoggingContextValue = {},
  branchContextValue = {},
  experimentContextValue = {},
  featuresContextValue = {}
}: MockedContextProviderProps) => {
  return (
    <AnalyticsProvider {...{ track: () => {}, ...analyticsContextValue }}>
      <TranslationProvider
        {...{
          t: (key) => {
            return `${key}`;
          },
          getLanguage: () => {
            return LOCALE.enUS;
          },
          ...translationContextValue
        }}
      >
        <ErrorLoggingProvider
          {...{
            logError: (e, _) => devLog(e),
            logMessage: (m, _) => {
              devLog(m);
              return m;
            },
            setTag: () => {},
            setTags: () => {},
            withScope: () => {},
            setContext: () => {},
            ...errorLoggingContextValue
          }}
        >
          <BranchProvider
            {...{
              isProcessing: false,
              createLink: () => {},
              appLink: 'mock_app_link',
              isReady: true,
              hasApp: true,
              handleLegacyBranchInit: (..._) => {},
              setIdentity: (_) => {},
              identityHash: 'mock_identity_hash',
              error: null,
              ...branchContextValue
            }}
          >
            <ExperimentProvider
              {...{
                getExperiment: (name) => ({
                  cohort: 'mocked_cohort',
                  experiment_name: name ?? 'mocked_experiment_name'
                }),
                ...experimentContextValue
              }}
            >
              <FeaturesProvider {...{ features: {}, ...featuresContextValue }}>
                {children}
              </FeaturesProvider>
            </ExperimentProvider>
          </BranchProvider>
        </ErrorLoggingProvider>
      </TranslationProvider>
    </AnalyticsProvider>
  );
};
